import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  ActivityIndicator,
  TextInput,
  TouchableOpacity,
  Modal,
  ScrollView,
  Alert,
  useWindowDimensions,
} from 'react-native';
import SvgIcon from '../components/SvgIcon';
import colors from '../theme/colors';
import profileDetailStyles from '../theme/profileDetailStyles';
import api from '../services/api';
import {
  DRINKING_OPTIONS,
  SMOKING_OPTIONS,
} from '../constants/profileOptions';

const MoreInfoTabScreen = ({ memberId, isOwnProfile, isMediatorProfile, onEditStateChange }) => {
  const { width, height } = useWindowDimensions();
  const isLandscape = width > height;
  
  const [additionalData, setAdditionalData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editData, setEditData] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [focusedInput, setFocusedInput] = useState(null);
  const [dropdownVisible, setDropdownVisible] = useState(false);
  const [activeDropdownField, setActiveDropdownField] = useState(null);
  const [activeDropdownOptions, setActiveDropdownOptions] = useState([]);

  useEffect(() => {
    loadAdditionalData();
  }, [memberId]);

  useEffect(() => {
    if (isEditMode && onEditStateChange && Object.keys(editData).length > 0) {
      onEditStateChange({
        isEditing: true,
        hasUnsavedChanges: true,
        onSave: handleSave,
        onCancel: handleCancelEdit,
      });
    }
  }, [editData, isEditMode]);

  const loadAdditionalData = async () => {
    setIsLoading(true);
    try {
      const response = await api.get(`/api/EnhancedMember/${memberId}/additional`);
      const data = response.data?.data || response.data;
      // Always set data even if null/empty to allow editing
      setAdditionalData(data || {});
    } catch (error) {
      console.error('Error loading additional data:', error);
      // Set empty object to allow editing even on error
      setAdditionalData({});
    } finally {
      setIsLoading(false);
    }
  };

  const handleEdit = () => {
    const additional = additionalData?.additionalDetails || additionalData || {};
    setEditData(additional);
    setIsEditMode(true);
    if (onEditStateChange) {
      onEditStateChange({
        isEditing: true,
        hasUnsavedChanges: false,
        onSave: handleSave,
        onCancel: handleCancelEdit,
      });
    }
  };

  const handleCancelEdit = () => {
    setIsEditMode(false);
    setEditData({});
    if (onEditStateChange) {
      onEditStateChange({
        isEditing: false,
        hasUnsavedChanges: false,
        onSave: null,
        onCancel: null,
      });
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const response = await api.put(`/api/EnhancedMember/${memberId}/additional`, editData);
      setAdditionalData(response.data?.data || response.data);
      Alert.alert('Success', 'Additional details updated successfully');
      setIsEditMode(false);
      setEditData({});
      if (onEditStateChange) {
        onEditStateChange({
          isEditing: false,
          hasUnsavedChanges: false,
          onSave: null,
          onCancel: null,
        });
      }
      return true;
    } catch (error) {
      console.error('Error saving additional data:', error);
      Alert.alert('Error', error.response?.data?.message || 'Failed to save changes');
      return false;
    } finally {
      setIsSaving(false);
    }
  };

  const getOptionsForField = (fieldKey) => {
    const optionsMap = {
      drinking: DRINKING_OPTIONS,
      smoking: SMOKING_OPTIONS,
    };
    return optionsMap[fieldKey] || [];
  };

  const openDropdown = (fieldKey, label) => {
    setActiveDropdownField({ key: fieldKey, label });
    setActiveDropdownOptions(getOptionsForField(fieldKey));
    setDropdownVisible(true);
  };

  const selectDropdownOption = (value) => {
    if (activeDropdownField) {
      setEditData({
        ...editData,
        [activeDropdownField.key]: value
      });
    }
    setDropdownVisible(false);
  };

  const renderSectionTitle = (title) => (
    <Text style={profileDetailStyles.sectionHeader}>{title}</Text>
  );

  const renderInfoRow = (label, value, fieldKey, editable = true, useDropdown = false) => {
    const isEditing = isEditMode && editable;
    const additional = additionalData?.additionalDetails || additionalData;
    
    if (!isEditing) {
      return (
        <View style={[profileDetailStyles.infoItem, isLandscape && profileDetailStyles.infoItemLandscape]}>
          <View style={profileDetailStyles.inputWrapper}>
            <Text style={profileDetailStyles.floatingLabel}>{label}</Text>
            <View style={profileDetailStyles.viewModeContainer}>
              <Text style={profileDetailStyles.viewModeText}>{value || 'Not Provided'}</Text>
            </View>
          </View>
        </View>
      );
    }

    // Edit mode with dropdown
    if (useDropdown) {
      const placeholderText = (fieldKey === 'smoking' || fieldKey === 'drinking') 
        ? 'Select Option' 
        : `Select ${label}`;
      
      return (
        <View style={[profileDetailStyles.infoItem, isLandscape && profileDetailStyles.infoItemLandscape]}>
          <View style={profileDetailStyles.inputWrapper}>
            <Text style={profileDetailStyles.floatingLabel}>{label}</Text>
            <TouchableOpacity 
              style={profileDetailStyles.dropdown}
              onPress={() => openDropdown(fieldKey, label)}
            >
              <Text style={(fieldKey in editData ? editData[fieldKey] : value) ? profileDetailStyles.dropdownText : profileDetailStyles.dropdownPlaceholder}>
                {fieldKey in editData ? (editData[fieldKey] || placeholderText) : (value || placeholderText)}
              </Text>
              <SvgIcon name="chevron-down" size={20} color={colors.textSecondary} />
            </TouchableOpacity>
          </View>
        </View>
      );
    }

    // Edit mode with text input
    return (
      <View style={[profileDetailStyles.infoItem, isLandscape && profileDetailStyles.infoItemLandscape]}>
        <View style={profileDetailStyles.inputWrapper}>
          <Text style={profileDetailStyles.floatingLabel}>{label}</Text>
          <TextInput
            style={profileDetailStyles.input}
            value={fieldKey in editData ? (editData[fieldKey]?.toString() || '') : (value?.toString() || '')}
            onChangeText={(text) => setEditData({ ...editData, [fieldKey]: text })}
            placeholder={`Enter ${label}`}
            placeholderTextColor={colors.textSecondary}
            multiline={fieldKey === 'otherDetails' || fieldKey === 'specialInterests'}
          />
        </View>
      </View>
    );
  };

  if (isLoading) {
    return (
      <View style={profileDetailStyles.loadingContainer}>
        <ActivityIndicator size="large" color={colors.accent} />
        <Text style={profileDetailStyles.loadingText}>Loading additional details...</Text>
      </View>
    );
  }

  if (!additionalData) {
    return (
      <View style={profileDetailStyles.container}>
        <Text style={profileDetailStyles.noDataText}>No additional details data available</Text>
      </View>
    );
  }

  const additional = additionalData.additionalDetails || additionalData;

  return (
    <View style={profileDetailStyles.container}>
      <ScrollView style={profileDetailStyles.scrollView} contentContainerStyle={profileDetailStyles.scrollContent}>
        {renderSectionTitle('Additional Details')}
        <View style={[profileDetailStyles.infoGrid, isLandscape && profileDetailStyles.infoGridLandscape]}>
          {renderInfoRow('Hobbies and Interests', additional.hobbiesAndInterests, 'hobbiesAndInterests', true, false)}
          {renderInfoRow('Sports', additional.sports, 'sports', true, false)}
          {renderInfoRow('Favorite Movies', additional.favoriteMovies, 'favoriteMovies')}
          {renderInfoRow('Favorite Music', additional.favoriteMusic, 'favoriteMusic')}
          {renderInfoRow('Spoken Languages', additional.spokenLanguages, 'spokenLanguages', true, false)}
          {renderInfoRow('Smoking', additional.smoking, 'smoking', true, true)}
          {renderInfoRow('Drinking', additional.drinking, 'drinking', true, true)}
          {renderInfoRow('Special Interests', additional.specialInterests, 'specialInterests')}
          {renderInfoRow('Additional Details', additional.otherDetails, 'otherDetails')}
        </View>
      </ScrollView>

      {/* Floating Edit Buttons */}
      {(isOwnProfile || isMediatorProfile) && (
        <View style={profileDetailStyles.floatingButtonContainer}>
          {isEditMode ? (
            <>
              <TouchableOpacity 
                onPress={handleCancelEdit} 
                style={[profileDetailStyles.floatingButton, profileDetailStyles.floatingCancelButton]}
                disabled={isSaving}
              >
                <SvgIcon name="close" size={16} color={colors.white} />
              </TouchableOpacity>
              <TouchableOpacity 
                onPress={handleSave} 
                style={[profileDetailStyles.floatingButton, profileDetailStyles.floatingSaveButton]}
                disabled={isSaving}
              >
                {isSaving ? (
                  <ActivityIndicator size="small" color={colors.white} />
                ) : (
                  <SvgIcon name="checkmark" size={16} color={colors.white} />
                )}
              </TouchableOpacity>
            </>
          ) : (
            <TouchableOpacity 
              onPress={handleEdit} 
              style={[profileDetailStyles.floatingButton, profileDetailStyles.floatingEditButton]}
            >
              <SvgIcon name="create-outline" size={16} color={colors.white} />
            </TouchableOpacity>
          )}
        </View>
      )}

      {/* Dropdown Modal */}
      <Modal
        visible={dropdownVisible}
        transparent={true}
        animationType="slide"
        onRequestClose={() => setDropdownVisible(false)}
      >
        <TouchableOpacity 
          style={profileDetailStyles.modalOverlay}
          activeOpacity={1}
          onPress={() => setDropdownVisible(false)}
        >
          <View style={profileDetailStyles.modalContent}>
            <View style={profileDetailStyles.modalHeader}>
              <Text style={profileDetailStyles.modalTitle}>
                {activeDropdownField?.label || 'Select'}
              </Text>
              <TouchableOpacity onPress={() => setDropdownVisible(false)}>
                <SvgIcon name="close" size={24} color={colors.white} />
              </TouchableOpacity>
            </View>
            <ScrollView style={profileDetailStyles.modalScroll}>
              {activeDropdownOptions.map((option) => (
                <TouchableOpacity
                  key={option.value}
                  style={profileDetailStyles.modalItem}
                  onPress={() => selectDropdownOption(option.value)}
                >
                  <Text style={profileDetailStyles.modalItemText}>{option.label}</Text>
                  {(editData[activeDropdownField?.key] || additional?.[activeDropdownField?.key]) === option.value && (
                    <SvgIcon name="checkmark-circle" size={20} color={colors.accent} />
                  )}
                </TouchableOpacity>
              ))}
            </ScrollView>
          </View>
        </TouchableOpacity>
      </Modal>
    </View>
  );
};

export default MoreInfoTabScreen;
